import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useLanguage } from "@/contexts/LanguageContext";
import { toast } from "sonner";
import { z } from "zod";

interface ContactDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const MESSAGE_LIMIT = 1000;

const ContactDialog = ({ open, onOpenChange }: ContactDialogProps) => {
  const { language, t } = useLanguage();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [messageValue, setMessageValue] = useState("");

  const contactSchema = z.object({
    name: z.string().trim().min(1, { message: language === "is" ? "Nafn er nauðsynlegt" : "Name is required" }).max(100),
    company: z.string().trim().min(1, { message: language === "is" ? "Nafn fyrirtækis er nauðsynlegt" : "Company name is required" }).max(100),
    email: z.string().trim().email({ message: language === "is" ? "Ógilt netfang" : "Invalid email address" }).max(255),
    phone: z.string().trim().max(20).optional(),
    message: z.string().trim().min(1, { message: language === "is" ? "Skilaboð eru nauðsynleg" : "Message is required" }).max(MESSAGE_LIMIT),
  });

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);

    const form = e.currentTarget;
    const formData = new FormData(form);
    const data = {
      name: formData.get("name") as string,
      company: formData.get("company") as string,
      email: formData.get("email") as string,
      phone: formData.get("phone") as string,
      message: formData.get("message") as string,
    };

    try {
      const validated = contactSchema.parse(data);

      console.log("Submitting to /api/contact:", validated);

      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(validated),
      });

      console.log("Response status:", res.status);

      if (!res.ok) {
        const errorText = await res.text();
        console.error("Error response:", errorText);
        throw new Error(`Server error: ${res.status}`);
      }

      const result = await res.json();
      console.log("Success result:", result);

      if (!result.ok) {
        throw new Error("Send failed");
      }

      form.reset();
      setMessageValue("");
      onOpenChange(false);

      toast.success(
        language === "is" ? "Skilaboð send" : "Message sent",
        {
          description: language === "is"
            ? "Við munum hafa samband við þig fljótlega."
            : "We'll get back to you shortly.",
        }
      );
    } catch (error) {
      console.error("Submit error:", error);
      if (error instanceof z.ZodError) {
        toast.error(language === "is" ? "Villa" : "Error", {
          description: error.errors[0].message,
        });
      } else {
        toast.error(language === "is" ? "Villa" : "Error", {
          description: language === "is"
            ? "Villa kom upp við sendingu. Vinsamlegast reyndu aftur."
            : "An error occurred. Please try again.",
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{language === "is" ? "Hafðu samband" : "Contact Us"}</DialogTitle>
          <DialogDescription>
            {language === "is" 
              ? "Fylltu út formið hér að neðan og við munum hafa samband við þig fljótlega."
              : "Fill out the form below and we'll get back to you shortly."}
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">
              {language === "is" ? "Nafn tengiliðar" : "Your name"}
            </Label>
            <Input id="name" name="name" required maxLength={100} />
          </div>

          <div className="space-y-2">
            <Label htmlFor="company">
              {language === "is" ? "Nafn fyrirtækis" : "Company name"}
            </Label>
            <Input id="company" name="company" required maxLength={100} />
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">
              {language === "is" ? "Netfang" : "Email"}
            </Label>
            <Input id="email" name="email" type="email" required maxLength={255} />
          </div>

          <div className="space-y-2">
            <Label htmlFor="phone">
              {language === "is" ? "Símanúmer (valkvætt)" : "Phone number (optional)"}
            </Label>
            <Input id="phone" name="phone" type="tel" maxLength={20} />
          </div>

          <div className="space-y-2">
            <Label htmlFor="message">
              {language === "is" ? "Hvernig getum við hjálpað?" : "How can we help?"}
            </Label>
            <Textarea
              id="message"
              name="message"
              required
              maxLength={MESSAGE_LIMIT}
              className="min-h-[120px]"
              value={messageValue}
              onChange={e => setMessageValue(e.target.value.slice(0, MESSAGE_LIMIT))}
            />
            <div className="text-xs text-muted-foreground text-right mt-1">
              {messageValue.length}/{MESSAGE_LIMIT}
            </div>
          </div>

          <Button type="submit" className="w-full" disabled={isSubmitting}>
            {isSubmitting 
              ? (language === "is" ? "Sendir..." : "Sending...") 
              : (language === "is" ? "Senda skilaboð" : "Send message")}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ContactDialog;
